import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import _get from 'lodash/get'
import shop from './shop'

const eventBus = new Vue()
const canHover = window.matchMedia('(hover:hover)').matches

Vue.use(Vuex)

export default new Vuex.Store({
  modules: {
    shop: shop
  },
  state: {
    ui: 'app-text',
    bus: eventBus,
    transition: 'pagefade',
    winW: window.innerWidth,
    winH: window.innerHeight,
    canHover: canHover,
    scrollLocked: false,

    theme: {
      bg: '#ffffff',
      text: '#0a0a0a',
      accent: '#1106b8'
    },
    changeTheme: null,

    site: null,
    pages: {},
    projects: [],

    articles: [],
    categories: [],
    feedPage: 1,
    feedEnd: false,

    subscribe: false, // hide/show overlay
    acceptedCookies: window.localStorage.getItem('bms_accepted_cookies')
  },
  getters: {
    winIs: (state) => (name) => {
      // match breakpoints in src/style/_settings.css
      return name === 'md' ? state.winW >= 768
        : name === 'lg' ? state.winW >= 1024
          : undefined
    },
    meta: (state) => ({ title, descrip, img }) => {
      const meta = []
      title = title ? `${title} - Blue Mountain School` : 'Blue Mountain School'
      // add
      meta.push({ property: 'og:title', content: title })
      meta.push({ property: 'og:site_name', content: 'Blue Mountain School' })
      meta.push({ property: 'og:type', content: 'website' })
      // meta description
      if (descrip !== false) {
        descrip = descrip || (state.site && state.site.site_description)
        meta.push({ name: 'description', content: descrip })
        meta.push({ property: 'og:description', content: descrip })
      }
      // image
      img = img || _get(state.site, 'site_meta_image')
      if (!img) return meta
      img = `${Vue.config.imgs.baseUrl}/image/fetch/${img}`
      meta.push({ property: 'og:image', content: img })
      //
      return meta
    }
  },
  mutations: {
    SET_UI (state, val = 'app-text') {
      state.ui = val
    },
    SET_WIN_W (state, val) {
      state.winW = val
    },
    SET_WIN_H (state, val) {
      state.winH = val
    },
    SET_SITE (state, data) {
      state.site = data
    },
    CHANGE_THEME (state, data) {
      state.changeTheme = data
    },
    ADD_PAGE (state, { slug, data }) {
      state.pages = { ...state.pages, [slug]: data }
    },
    ADD_ARTICLES (state, { posts, totalPages }) {
      state.feedEnd = state.feedPage === Number(totalPages)
      state.articles.push(...posts)
      if (!state.feedEnd) state.feedPage++
    },
    ADD_PROJECTS (state, itms) {
      state.projects.push(...itms)
    },
    SET_CATEGORIES (state, cats) {
      state.categories = cats.map(({ id, name, slug, parent }) => { return { name, id, slug, parent } })
    },
    SET_SUBSCRIBE (state, visible) {
      state.subscribe = visible
    },
    ACCEPT_COOKIES (state) {
      state.acceptedCookies = new Date()
      window.localStorage.setItem('bms_accepted_cookies', state.acceptedCookies)
      return this.$ga && this.$ga.enable() // enable vue-analytics tracking
    },
    SET_PAGE_TRANSITION (state, trans) {
      state.transition = trans || 'pagefade'
    },
    lockScroll (state, bool) {
      state.scrollLocked = bool
    }
  },
  actions: {
    async getGlobals ({ state, commit }) {
      return axios.get(Vue.config.WP.ACF + '/options/options')
        .then(({ data }) => {
          console.log(data)
          commit('SET_SITE', data.acf)
        })
    },
    async getPage ({ state, commit, dispatch }, needle) {
      // find doc by slug or id
      const saved = state.pages[needle] || Object.values(state.pages).find(doc => doc.id === needle)
      if (saved) return state.pages[saved.slug]
      const searchBy = typeof needle === 'number' ? '?include=' : '?slug='
      return axios.get(Vue.config.WP.API + `/pages${searchBy}${needle}`)
        .then(({ data }) => {
          const doc = data[0]
          if (!doc) return null
          commit('ADD_PAGE', { slug: doc.slug, data: doc })
          if (doc.parent) dispatch('getPage', doc.parent) // get parent for breadcrumb nav
          return doc
        })
    },
    async getArticles ({ state, commit }) {
      if (state.feedEnd) return false
      return axios.get(Vue.config.WP.API + `/posts?page=${state.feedPage}&per_page=20&_embed`)
        .then(resp => {
          commit('ADD_ARTICLES', { posts: resp.data, totalPages: resp.headers['x-wp-totalpages'] })
        })
    },
    async getArticlesInCategory ({ state, commit, dispatch }, { cat, pg }) {
      const category = state.categories.find(itm => itm.slug === cat)
      if (!category) return dispatch('getCategories').then(() => dispatch('getArticlesInCategory', { cat, pg }))
      return axios.get(Vue.config.WP.API + `/posts?categories=${category.id}&page=${pg}&per_page=20&_embed`)
        .then(resp => {
          return { posts: resp.data, totalPages: resp.headers['x-wp-totalpages'] }
        })
    },
    async getArticle ({ state }, slug) {
      const svd = state.articles.find(doc => doc.slug === slug)
      if (svd) return svd
      return axios.get(Vue.config.WP.API + '/posts?slug=' + slug)
        .then(({ data }) => data[0])
    },
    async getPreview (_, { id, preview, type }) {
      return axios.get(`${Vue.config.WP.API}/${type}/${id}/revisions?_wpnonce=${preview}`,
        { withCredentials: true } // REQUIRED !!!!
      ).then(({ data }) => data[0])
    },
    async getArticleCollection (_, id) {
      return axios.get(Vue.config.WP.API + '/post-sets/' + id)
        .then(resp => resp.data)
    },
    async getBlueProject ({ state }, slug) {
      const svd = state.projects.find(doc => doc.slug === slug)
      return svd || axios.get(Vue.config.WP.API + '/blue-projects?slug=' + slug)
        .then(({ data }) => data[0])
    },
    async getBlueProjects ({ state, commit }) {
      if (state.projects.length) return false
      return axios.get(Vue.config.WP.API + `/blue-projects?per_page=100&_embed`)
        .then(({ data }) => {
          commit('ADD_PROJECTS', data)
        })
    },
    async getCategories ({ state, commit }) {
      if (state.categories.length) return
      return axios.get(Vue.config.WP.API + '/categories?per_page=100') // &hide_empty=true')
        .then(({ data }) => commit('SET_CATEGORIES', data))
    },
    async getMedia (_, { id, wpnonce }) {
      if (!id) return console.warn('Media ID missing')
      let url = Vue.config.WP.API + `/media/${id}`
      // add nonce for previewing videos attached to private pages
      url += wpnonce ? `?_wpnonce=${wpnonce}` : ''
      return axios.get(url, { withCredentials: true }).then(resp => {
        return formatMedia(resp.data)
      })
    },
    async addSubscriber (context, data) {
      console.log(data)
      // go !
      return axios.post('/add-subscriber', data)
        .then(resp => console.log(resp))
        .catch(err => {
          console.log(err.response.data)
          let msg = err.response.data.title || "We're sorry, an error occurred. Please try again."
          // reformat errors
          msg = msg === 'Member Exists' ? 'Already subscribed.'
            : msg === 'Invalid Resource' ? 'Please include your name.'
              : msg === 'Forgotten Email Not Subscribed' ? "Sorry, we can't re-subscribe that email address through this form. Please use a different email address or contact info@bluemountain.school for a confirmation link."
                : msg
          return Promise.reject(new Error(msg))
        })
    }
  }
})

function formatMedia (media) {
  /*
   * Function to parse alternate video sizes
  */
  const type = (media && media.mime_type) || ''
  if (type.startsWith('video')) {
    // TODO - use regex insetad of innerHTML so the element doesnt load the sources (safari)...
    // video sizes written into Media post's the_content() in `description.rendered`
    // so pull from DOM el
    const el = document.createElement('div')
    // change `src` attr to `data-src` so doesn't download right away
    el.innerHTML = media.description.rendered.replace(/<source src=/g, '<source data-src=')
    // set poster
    const video = el.querySelector('video')
    media.poster = video && video.getAttribute('poster')
    // set sources
    const srcs = el.querySelectorAll('source')
    const arr = []
    srcs.forEach(el => {
      if (!el || !el.getAttribute('data-src')) return
      // [width, src]
      arr.push([parseInt(el.getAttribute('data-res')), el.getAttribute('data-src')])
    })
    media.sources = arr.reverse()
  }
  return media
}
